import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Alert, Typography } from '@mui/material';
import Header from './components/Header';
import ItemListPage from './pages/ItemListPage';
import ItemEditorPage from './pages/ItemEditorPage';
import PackageListPage from './pages/PackageListPage';
import PackageQuestionsPage from './pages/PackageQuestionsPage';
import LoginPage from './pages/LoginPage';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { verifyToken, logout } from './store/slices/authSlice';

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, error } = useSelector((state: RootState) => state.auth);

  // Verify token on app load
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && !user) {
      dispatch(verifyToken() as any);
    }
  }, [dispatch, user]);

  // Check for role-based access errors
  useEffect(() => {
    if (error && error.includes('CMS_ADMIN')) {
      // Auto-logout users without proper role
      dispatch(logout() as any);
    }
  }, [error, dispatch]);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Additional check for CMS_ADMIN role
  const portals: string[] = (user as any)?.roles?.length ? (user as any).roles : user ? [user.role] : [];
  if (user && !portals.includes('CMS_ADMIN')) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        p: 3
      }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            You don't have permission to access the Content Management System.
            <br />
            <strong>Required role:</strong> CMS_ADMIN
            <br />
            <strong>Your role:</strong> {user.role || 'None'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Contact your administrator to request CMS access.
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      maxWidth: '1400px', // Limit overall width like UMS/LMS
      margin: '0 auto', // Center the content
      boxShadow: '0 0 20px rgba(0,0,0,0.1)', // Subtle shadow for definition
    }}>
      <Header />
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Routes>
          <Route path="/" element={<Navigate to="/items" replace />} />
          <Route path="/items" element={<ItemListPage />} />
          <Route path="/items/new" element={<ItemEditorPage />} />
          <Route path="/items/:id/edit" element={<ItemEditorPage />} />
          <Route path="/tests" element={<PackageListPage />} />
          <Route path="/tests/:id" element={<PackageQuestionsPage />} />
          <Route path="*" element={<Navigate to="/items" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default App;
